import React from 'react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { Box, Typography } from '@mui/material';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import colorFinder from '../../utils/colorFinder';

const baseStyle = {
  caption: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: 'white',
  },
  textColor: {
    color: '#667280',
  },
  biggerFontSize: {
    fontSize: '2rem',
  },
  smallerFontSize: {
    fontSize: '1.1rem',
  },
  smallFontSize: {
    fontSize: '1rem',
  },
  typographyHeaderColor: {
    color: 'white',
  },
};

const textColorForBg = (hex) => {
  if (!hex || !/^#?[0-9a-fA-F]{6}$/.test(hex)) return '#fff';
  const clean = hex.startsWith('#') ? hex.slice(1) : hex;
  const val = parseInt(clean, 16);
  return val > 0xffffff / 2 ? '#000' : '#fff';
};

//@TODO: Kitenni külső fileba a formázást, és végig verni ahol most sx-ben bele van égetve a css.

const ProgressMapTree = ({ rows, selectedRowState }) => {
  const { selectedRow, setSelectedRow } = selectedRowState;

  return (
    <Box>
      <SimpleTreeView
        selectionMode="single"
        selectedItems={selectedRow ? [selectedRow] : []}
        onSelectedItemsChange={(_, ids) => setSelectedRow(ids[0] ?? null)}
        sx={{
          '& .MuiTreeItem-root.Mui-selected > .MuiTreeItem-content': { backgroundColor: '#feac3140 !important' },
          '& .MuiTreeItem-root.Mui-selected.Mui-focusVisible > .MuiTreeItem-content': {
            backgroundColor: '#feac3140 !important',
          },
          '& .MuiTreeItem-content.Mui-focused': { backgroundColor: '#feac3140 !important' },
          '& .MuiTreeItem-content:hover': { backgroundColor: '#feac311a' },
          '& .MuiTreeItem-root.Mui-selected .MuiTreeItem-label': { backgroundColor: 'transparent !important' },
          '& .MuiTreeItem-content': { padding: '4px 0 0px 0' },
        }}
      >
        {rows.map((ref) => (
          <TreeItem
            key={`REF_${ref.reference}`}
            itemId={`REF_${ref.reference}`}
            label={
              <Typography
                variant="h6"
                fontWeight="bold"
                borderBottom="1px solid #667280"
                sx={{ ...baseStyle.textColor, ...baseStyle.biggerFontSize }}
              >
                {ref.reference}
              </Typography>
            }
          >
            {ref.documents.map((doc, docIdx) => (
              <TreeItem
                key={`REF_${ref.reference}__DOC_${doc.docId}`}
                itemId={`REF_${ref.reference}__DOC_${doc.docId}`}
                label={
                  <Box
                    mt={1}
                    pt={docIdx !== 0 ? 1 : 0}
                    borderTop={docIdx !== 0 ? '1px solid #667280' : 'none'}
                    sx={{ ...baseStyle.textColor }}
                  >
                    <Box display="flex" gap="2.5rem" bgcolor="rgba(49, 131, 254, 0.8)" p="0.5rem 1rem">
                      <Box display="flex" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ ...baseStyle.caption }}>
                            Rendelési szám
                          </Typography>
                          <Typography sx={{ ...baseStyle.typographyHeaderColor }}>{doc.docNum}</Typography>
                        </Box>
                        <TrendingFlatIcon
                          sx={{ ...baseStyle.biggerFontSize, ...baseStyle.typographyHeaderColor, ml: 1 }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ ...baseStyle.caption }}>
                          Határidő
                        </Typography>
                        <Typography sx={{ ...baseStyle.typographyHeaderColor }}>{doc.dueDate}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ ...baseStyle.caption }}>
                          Vevő neve
                        </Typography>
                        <Typography sx={{ ...baseStyle.typographyHeaderColor }}>{doc.companyName}</Typography>
                      </Box>
                    </Box>
                  </Box>
                }
              >
                {doc.items.map((item) => {
                  const bg = item.hexa ?? 'transparent';
                  const fg = textColorForBg(item.hexa);
                  const itemKey = `${item.itemCode ?? ''}__${item.productionReference ?? ''}`;
                  return (
                    <TreeItem
                      key={`REF_${ref.reference}__DOC_${doc.docId}__ITEM_${itemKey}`}
                      itemId={`REF_${ref.reference}__DOC_${doc.docId}__ITEM_${itemKey}`}
                      label={
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                          sx={{ backgroundColor: bg, color: fg }}
                        >
                          <Typography sx={{ ...baseStyle.smallFontSize }} fontWeight="bold" pl="0.5rem">
                            {`${item.itemCode} - ${item.itemName} - ${item.productionReference}`}
                          </Typography>
                          <Box display="flex" gap="0.5rem">
                            <Typography sx={{ backgroundColor: bg, color: fg, borderRadius: '4px' }} p="0.5rem">
                              {item.qntStatus}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    >
                      {item.productions.map((prod) => (
                        <TreeItem
                          key={`REF_${ref.reference}__DOC_${doc.docId}__ITEM_${itemKey}__PROD_${prod.productionId}`}
                          itemId={`REF_${ref.reference}__DOC_${doc.docId}__ITEM_${itemKey}__PROD_${prod.productionId}`}
                          label={
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography>{`${prod.workingNumber} - ${prod.manufecturedItemCode}`}</Typography>
                              <Typography
                                sx={{
                                  backgroundColor: colorFinder(prod?.prodidcolor)?.colorBgCode,
                                  color: 'black',
                                  borderRadius: '4px',
                                }}
                                p="0.5rem"
                              >
                                {prod.prodidstatus}
                              </Typography>
                            </Box>
                          }
                        >
                          {prod.levels.map((lvl, idx) => (
                            <TreeItem
                              sx={{
                                background: idx % 2 === 0 ? 'inherit' : '#ecececcb',
                                '& .MuiTreeItem-content': { borderTop: '1px solid #ddd' },
                              }}
                              key={`REF_${ref.reference}__DOC_${doc.docId}__ITEM_${itemKey}__PROD_${prod.productionId}__LVL_${lvl.level}_${idx}`}
                              itemId={`REF_${ref.reference}__DOC_${doc.docId}__ITEM_${itemKey}__PROD_${prod.productionId}__LVL_${lvl.level}_${idx}`}
                              label={
                                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                  <Typography flex="1">{lvl.operationName}</Typography>
                                  {lvl.nickName && (
                                    <Box display="flex" gap="0.5rem" flex="1">
                                      <Typography>{`${lvl.nickName} - ${lvl.endDate}`}</Typography>
                                    </Box>
                                  )}
                                  <Typography
                                    sx={{
                                      backgroundColor: colorFinder(lvl?.prodStatusColor)?.colorBgCode,
                                      color: 'black',
                                      borderRadius: '4px',
                                    }}
                                    p="0.5rem"
                                  >
                                    {lvl.prodStatusName}
                                  </Typography>
                                </Box>
                              }
                            />
                          ))}
                        </TreeItem>
                      ))}
                    </TreeItem>
                  );
                })}
              </TreeItem>
            ))}
          </TreeItem>
        ))}
      </SimpleTreeView>
    </Box>
  );
};

export default ProgressMapTree;
